// Dynamic configuration for profile completeness tracking
// This replaces static field definitions with dynamic, configurable values

// Profile sections - can be updated dynamically
export const PROFILE_SECTIONS = {
  COMPANY_INFORMATION: 'company_information',
  BUSINESS_MODEL: 'business_model',
  MARKET_ANALYSIS: 'market_analysis',
  TEAM_OPERATIONS: 'team_operations',
  FINANCIAL_INFORMATION: 'financial_information'
};

// Required sections for profile completion - can be updated dynamically
export const REQUIRED_SECTIONS = [
  'company_information',
  'business_model',
  'market_analysis',
  'team_operations',
  'financial_information'
];

// Required fields per section - can be updated dynamically
export const REQUIRED_FIELDS = {
  company_information: [
    'name', 'email', 'overview', 'primary_industry', 'stage'
  ],
  business_model: [
    'business_model', 'revenue_model', 'value_proposition'
  ],
  market_analysis: [
    'market_size', 'target_audience', 'competitive_analysis'
  ],
  team_operations: [
    'founding_team'
  ],
  financial_information: [
    'base_currency', 'current_stage', 'funding_requirements', 'use_of_funds',
    'financial_projections', 'customer_traction'
  ]
};

// Optional fields per section - can be updated dynamically
export const OPTIONAL_FIELDS = {
  company_information: [
    'website', 'linkedin_url', 'facebook_url', 'registration_number', 'registration_date',
    'elevator_pitch', 'detailed_description', 'secondary_sectors', 'headquarters_address',
    'additional_offices', 'legal_structure', 'incorporation_country',
    'incorporation_state', 'incorporation_date', 'patents', 'trademarks',
    'copyrights'
  ],
  business_model: [
    'pricing_strategy', 'distribution_channels', 'key_partnerships',
    'go_to_market_strategy', 'customer_acquisition_plans', 'revenue_streams',
    'customer_acquisition_cost', 'lifetime_value', 'competitive_advantage',
    'employee_count'
  ],
  market_analysis: [
    'target_market', 'growth_rate', 'competitive_landscape',
    'barriers_to_entry', 'market_trends'
  ],
  team_operations: [
    'team_dynamics', 'equity_distribution'
  ],
  financial_information: [
    'current_financial_status', 'burn_rate', 'runway', 'funding_history',
    'current_investors', 'board_composition', 'banking_information',
    'projection_scenarios', 'historical_financials', 'key_financial_metrics',
    'break_even_date', 'unit_economics', 'profitability_timeline'
  ]
};

// Field validation rules - can be updated dynamically
export const FIELD_VALIDATION_RULES = {
  email: {
    type: 'email',
    required: true,
    pattern: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
    message: 'Please enter a valid email address'
  },
  phone: {
    type: 'phone',
    required: true,
    pattern: /^\d{10}$/,
    message: 'Phone number must be exactly 10 digits'
  },
  website: {
    type: 'url',
    required: false,
    pattern: /^https?:\/\/.+/,
    message: 'Please enter a valid website URL'
  },
  founded_date: {
    type: 'date',
    required: false,
    format: 'YYYY-MM-DD',
    message: 'Please enter a valid date'
  }
};

// Completion thresholds - can be updated dynamically
export const COMPLETION_THRESHOLDS = {
  profile_section: {
    minimum: 0,      // Minimum completion to access section
    recommended: 50,  // Recommended completion for better experience
    complete: 100    // Full completion
  },
  documents: {
    minimum: 80,      // 80% of required documents uploaded
    recommended: 90,  // 90% for better review process
    complete: 100     // All required documents uploaded
  },
  overall_profile: {
    minimum: 60,      // 60% overall completion to enable submission
    recommended: 80,  // 80% for better incubator review
    complete: 100     // 100% complete profile
  }
};

// Section completion time estimates (in minutes) - can be updated dynamically
export const SECTION_COMPLETION_TIMES = {
  company_information: {
    basic: 15,        // Basic required fields
    detailed: 30,     // With optional fields
    comprehensive: 45 // With all fields and validation
  },
  business_model: {
    basic: 20,
    detailed: 40,
    comprehensive: 60
  },
  market_analysis: {
    basic: 25,
    detailed: 50,
    comprehensive: 75
  },
  team_operations: {
    basic: 20,
    detailed: 35,
    comprehensive: 50
  },
  financial_information: {
    basic: 30,
    detailed: 60,
    comprehensive: 90
  }
};

// Progress tracking configuration - can be updated dynamically
export const PROGRESS_TRACKING_CONFIG = {
  autoSaveInterval: 30,  // Auto-save every 30 seconds
  progressUpdateInterval: 5,  // Update progress every 5 seconds
  completionCheckInterval: 10,  // Check completion every 10 seconds
  sessionTimeout: 3600,  // Session timeout in seconds (1 hour)
  draftRetentionDays: 30,  // Keep drafts for 30 days
  backupFrequency: 300  // Backup every 5 minutes
};

// Helper functions for dynamic configuration
export const getProfileSections = () => {
  return Object.values(PROFILE_SECTIONS);
};

export const getRequiredSections = () => {
  return [...REQUIRED_SECTIONS];
};

export const getRequiredFields = (sectionName) => {
  return REQUIRED_FIELDS[sectionName] || [];
};

export const getOptionalFields = (sectionName) => {
  return OPTIONAL_FIELDS[sectionName] || [];
};

export const getAllFields = (sectionName) => {
  const required = getRequiredFields(sectionName);
  const optional = getOptionalFields(sectionName);
  return [...required, ...optional];
};

export const getFieldValidationRule = (fieldName) => {
  return FIELD_VALIDATION_RULES[fieldName] || {};
};

export const getCompletionThreshold = (category, level = 'minimum') => {
  return COMPLETION_THRESHOLDS[category]?.[level] || 0;
};

export const getSectionCompletionTime = (sectionName, completionLevel = 'basic') => {
  return SECTION_COMPLETION_TIMES[sectionName]?.[completionLevel] || 15;
};

export const getProgressTrackingConfig = () => {
  return PROGRESS_TRACKING_CONFIG;
};

export const validateSectionName = (sectionName) => {
  return Object.values(PROFILE_SECTIONS).includes(sectionName);
};

export const validateFieldName = (sectionName, fieldName) => {
  const allFields = getAllFields(sectionName);
  return allFields.includes(fieldName);
};

export const isRequiredField = (sectionName, fieldName) => {
  const requiredFields = getRequiredFields(sectionName);
  return requiredFields.includes(fieldName);
};

export const isOptionalField = (sectionName, fieldName) => {
  const optionalFields = getOptionalFields(sectionName);
  return optionalFields.includes(fieldName);
};

export const getSectionCompletionPercentage = (sectionData, sectionName) => {
  const requiredFields = getRequiredFields(sectionName);
  const optionalFields = getOptionalFields(sectionName);

  if (!sectionData) {
    return 0;
  }

  const hasMeaningfulValue = (value) => {
    if (value === null || value === undefined) {
      return false;
    }
    if (typeof value === 'string') {
      return value.trim() !== '';
    }
    if (Array.isArray(value)) {
      return value.length > 0 && value.some(item => hasMeaningfulValue(item));
    }
    if (typeof value === 'object') {
      return Object.keys(value).length > 0 && Object.values(value).some(v => hasMeaningfulValue(v));
    }
    return true;
  };

  // Calculate required fields completion
  let requiredCompleted = 0;
  requiredFields.forEach(field => {
    const value = sectionData[field];
    if (hasMeaningfulValue(value)) {
      // Additional validation for specific field types
      if (field === 'email' && typeof value === 'string') {
        if (value.includes('@') && value.split('@')[1]?.includes('.')) {
          requiredCompleted += 1;
        }
      } else if (field === 'overview' && typeof value === 'string') {
        if (value.trim().length >= 50) {
          requiredCompleted += 1;
        }
      } else if (field === 'founding_team' && Array.isArray(value)) {
        // For founding_team, check if at least one team member has meaningful data
        const hasValidMember = value.some(member => {
          if (typeof member === 'object' && member !== null) {
            // Check if member has at least name and one other meaningful field
            return member.name && member.name.trim() &&
              ['photo', 'bio', 'role', 'responsibilities', 'educational_background', 'work_background']
                .some(key => hasMeaningfulValue(member[key]));
          }
          return false;
        });
        if (hasValidMember) {
          requiredCompleted += 1;
        }
      } else if (field === 'organizational_structure' && typeof value === 'string') {
        if (value.trim() && value.trim() !== '') {
          requiredCompleted += 1;
        }
      } else {
        requiredCompleted += 1;
      }
    }
  });

  // Calculate optional fields completion
  let optionalCompleted = 0;
  optionalFields.forEach(field => {
    const value = sectionData[field];
    if (hasMeaningfulValue(value)) {
      optionalCompleted += 1;
    }
  });

  // If all required fields are completed, the section is 100% complete
  if (requiredCompleted === requiredFields.length) {
    return 100;
  }

  // Otherwise, use weighted calculation for partial completion
  const requiredPercentage = requiredFields.length > 0 ? (requiredCompleted / requiredFields.length) * 70 : 0;
  const optionalPercentage = optionalFields.length > 0 ? (optionalCompleted / optionalFields.length) * 30 : 0;

  return Math.round(requiredPercentage + optionalPercentage);
};

export const getOverallCompletionPercentage = (profileData) => {
  if (!profileData) {
    return 0;
  }

  let totalCompletion = 0;
  let sectionsCount = 0;

  getRequiredSections().forEach(sectionName => {
    const sectionData = profileData[sectionName]?.data || {};
    const sectionCompletion = getSectionCompletionPercentage(sectionData, sectionName);
    totalCompletion += sectionCompletion;
    sectionsCount += 1;
  });

  return sectionsCount > 0 ? Math.round(totalCompletion / sectionsCount) : 0;
};

export const canSubmitProfile = (profileData) => {
  const overallCompletion = getOverallCompletionPercentage(profileData);
  const minimumThreshold = getCompletionThreshold('overall_profile', 'minimum');
  return overallCompletion >= minimumThreshold;
};

export const getMissingRequiredFields = (profileData, sectionName = null) => {
  const missingFields = [];

  if (sectionName) {
    // Check specific section
    const sectionData = profileData[sectionName]?.data || {};
    const requiredFields = getRequiredFields(sectionName);

    requiredFields.forEach(field => {
      const value = sectionData[field];
      if (!value || value === "" ||
        (Array.isArray(value) && value.length === 0) ||
        (typeof value === 'object' && Object.keys(value).length === 0)) {
        missingFields.push(field);
      }
    });
  } else {
    // Check all sections
    getRequiredSections().forEach(section => {
      const sectionMissing = getMissingRequiredFields(profileData, section);
      missingFields.push(...sectionMissing.map(field => `${section}.${field}`));
    });
  }

  return missingFields;
};

export const estimateCompletionTime = (profileData) => {
  let totalTime = 0;

  getRequiredSections().forEach(sectionName => {
    const sectionData = profileData[sectionName]?.data || {};
    const completion = getSectionCompletionPercentage(sectionData, sectionName);

    if (completion < 100) {
      // Estimate remaining time based on completion level
      let remainingTime;
      if (completion < 50) {
        remainingTime = getSectionCompletionTime(sectionName, 'comprehensive');
      } else if (completion < 80) {
        remainingTime = getSectionCompletionTime(sectionName, 'detailed');
      } else {
        remainingTime = getSectionCompletionTime(sectionName, 'basic');
      }

      totalTime += remainingTime;
    }
  });

  return totalTime;
};

// Sector configuration for industry selection
export const PRIMARY_SECTORS = [
  "FinTech",
  "HealthTech",
  "EdTech",
  "AgriTech",
  "E-commerce",
  "SaaS",
  "AI / ML",
  "Manufacturing",
  "Clean Energy",
  "Logistics",
  "Real Estate",
  "Food & Beverages",
  "Consumer Goods",
  "Fashion",
  "Gaming",
  "Cybersecurity",
  "Media & Entertainment",
  "Travel & Hospitality",
  "Human Resources / HRTech",
  "LegalTech",
  "PropTech",
  "SportsTech",
  "Biotech",
  "Automotive / EV",
  "Blockchain / Web3",
  "Others"
];

export const SECONDARY_SECTORS = {
  "FinTech": [
    "Payments",
    "Lending",
    "WealthTech",
    "InsurTech",
    "RegTech",
    "Crypto / Web3 Finance",
    "Neobanking",
    "Other"
  ],

  "HealthTech": [
    "Telemedicine",
    "Medical Devices",
    "Diagnostics",
    "Pharma",
    "Health Insurance",
    "Fitness & Wellness",
    "Hospital Management",
    "Other"
  ],

  "EdTech": [
    "Online Courses",
    "Skill Development",
    "Learning Management Systems",
    "K-12 Learning",
    "Higher Education",
    "Test Preparation",
    "Other"
  ],

  "AgriTech": [
    "Farm Management",
    "Agri Supply Chain",
    "Agri Inputs",
    "Drones & IoT",
    "Agri Finance",
    "Other"
  ],

  "E-commerce": [
    "Marketplace",
    "D2C Brand",
    "Fashion",
    "Electronics",
    "Grocery",
    "Delivery",
    "Other"
  ],

  "SaaS": [
    "B2B Tools",
    "CRM",
    "ERP",
    "Analytics",
    "Automation",
    "Customer Support",
    "Other"
  ],

  "AI / ML": [
    "GenAI",
    "Computer Vision",
    "NLP",
    "Robotics",
    "AI Automation",
    "Predictive Analytics",
    "Other"
  ],

  "Manufacturing": [
    "Industrial Automation",
    "Machinery",
    "Electronics Manufacturing",
    "Quality Control",
    "Supply Chain",
    "Other"
  ],

  "Clean Energy": [
    "Solar",
    "EV Charging",
    "Wind",
    "Green Hydrogen",
    "Energy Storage",
    "Other"
  ],

  "Logistics": [
    "Warehousing",
    "Last-mile Delivery",
    "Freight",
    "Fleet Management",
    "Supply Chain Tech",
    "Other"
  ],

  "Real Estate": [
    "Construction",
    "Rentals",
    "Property Management",
    "Architecture",
    "Interior Tech",
    "Other"
  ],

  "Food & Beverages": [
    "Restaurant",
    "Food Delivery",
    "Cloud Kitchen",
    "Packaged Foods",
    "Beverages",
    "Other"
  ],

  "Consumer Goods": [
    "Home Care",
    "Personal Care",
    "Electronics",
    "Appliances",
    "D2C",
    "Other"
  ],

  "Fashion": [
    "Clothing",
    "Footwear",
    "Accessories",
    "Jewelry",
    "Luxury Fashion",
    "Other"
  ],

  "Gaming": [
    "Mobile Games",
    "PC / Console Games",
    "eSports",
    "Game Development Tools",
    "AR / VR Games",
    "Other"
  ],

  "Cybersecurity": [
    "Network Security",
    "Cloud Security",
    "App Security",
    "Threat Detection",
    "Incident Response",
    "Other"
  ],

  "Media & Entertainment": [
    "Content Creation",
    "OTT",
    "Music",
    "Influencer Tech",
    "Gaming Content",
    "Other"
  ],

  "Travel & Hospitality": [
    "Travel Booking",
    "Hotels",
    "Tourism",
    "Travel Tech",
    "Experiences",
    "Other"
  ],

  "Human Resources / HRTech": [
    "Recruitment",
    "HRMS",
    "Payroll",
    "Employee Engagement",
    "Performance Management",
    "Other"
  ],

  "LegalTech": [
    "Contracts",
    "Compliance",
    "Case Management",
    "Document Automation",
    "Other"
  ],

  "PropTech": [
    "Smart Homes",
    "Property Management",
    "Rental Platforms",
    "Construction Tech",
    "Other"
  ],

  "SportsTech": [
    "Athlete Analytics",
    "Fantasy Sports",
    "Sports Training",
    "Sports Equipment",
    "Other"
  ],

  "Biotech": [
    "Genomics",
    "Biopharma",
    "Diagnostics",
    "Bioinformatics",
    "Other"
  ],

  "Automotive / EV": [
    "EV Manufacturing",
    "Battery Tech",
    "Charging Infrastructure",
    "Autonomous Vehicles",
    "Auto Parts",
    "Other"
  ],

  "Blockchain / Web3": [
    "DeFi",
    "NFTs",
    "DAOs",
    "Crypto Tools",
    "Blockchain Infrastructure",
    "Other"
  ],

  "Others": ["Other"]
};

// Helper functions for sector configuration
export const getPrimarySectorOptions = () => {
  return PRIMARY_SECTORS.map(sector => ({
    value: sector,
    label: sector
  }));
};

export const getSecondarySectorOptions = (primarySectors) => {
  if (!primarySectors || !Array.isArray(primarySectors) || primarySectors.length === 0) {
    return [];
  }

  // Collect all secondary sectors from selected primary sectors
  const allSecondaries = new Set();
  primarySectors.forEach(primary => {
    const secondaries = SECONDARY_SECTORS[primary];
    if (secondaries && Array.isArray(secondaries)) {
      secondaries.forEach(secondary => allSecondaries.add(secondary));
    }
  });

  return Array.from(allSecondaries).map(sector => ({
    value: sector,
    label: sector
  }));
};

export const getAllSecondarySectors = () => {
  const allSecondaries = new Set();
  Object.values(SECONDARY_SECTORS).forEach(secondaries => {
    if (Array.isArray(secondaries)) {
      secondaries.forEach(secondary => allSecondaries.add(secondary));
    }
  });

  return Array.from(allSecondaries).map(sector => ({
    value: sector,
    label: sector
  }));
};

// Achievement categories for mentor achievements
export const ACHIEVEMENT_CATEGORIES = [
  'Funding',
  'Growth',
  'Product Development',
  'Market Expansion',
  'Team Building',
  'Strategic Planning',
  'Mentoring Excellence',
  'Industry Recognition',
  'Other'
];

// Helper function to get achievement category options
export const getAchievementCategoryOptions = () => {
  return ACHIEVEMENT_CATEGORIES.map(category => ({
    value: category,
    label: category
  }));
};

const profileProgressConfig = {
  PROFILE_SECTIONS,
  REQUIRED_SECTIONS,
  REQUIRED_FIELDS,
  OPTIONAL_FIELDS,
  FIELD_VALIDATION_RULES,
  COMPLETION_THRESHOLDS,
  SECTION_COMPLETION_TIMES,
  PROGRESS_TRACKING_CONFIG,
  PRIMARY_SECTORS,
  SECONDARY_SECTORS,
  ACHIEVEMENT_CATEGORIES,
};

export default profileProgressConfig;
